/**
 * AI helpers for the "Explora México" experience.
 *
 * - `matchDestinationsByText`: free-text input → ranked destination slugs.
 * - `recommendDestinations`: structured questionnaire → ranked destination slugs.
 *
 * Both are graceful: on any failure they return `{ slugs: [], reason: "..." }`
 * so the UI never shows a raw "Forbidden"/500.
 */

import { createServerFn } from "@tanstack/react-start";
import { getRequest } from "@tanstack/react-start/server";
import { z } from "zod";
import {
  EXPLORA_DESTINATIONS,
  EXPLORA_CATEGORIES,
  type ExploraCategory,
} from "./explora-destinations";
import { getClientIp, rateLimit } from "./ai-rate-limit.server";


const LOVABLE_AI_URL = "https://ai.gateway.lovable.dev/v1/chat/completions";

interface AiResult {
  slugs: string[];
  reason: string;
}

const ALL_SLUGS = EXPLORA_DESTINATIONS.map((d) => d.slug);

function buildDestinationContext(): string {
  return EXPLORA_DESTINATIONS.map(
    (d) =>
      `- ${d.slug} (${d.name}, ${d.state}): categorías=${d.categories.join(",")}. ${d.description.es}`,
  ).join("\n");
}

async function callAi(systemPrompt: string, userPrompt: string): Promise<AiResult> {
  const key = process.env.LOVABLE_API_KEY;
  if (!key) return { slugs: [], reason: "AI no configurada." };

  try {
    const res = await fetch(LOVABLE_AI_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${key}`,
      },
      body: JSON.stringify({
        model: "google/gemini-2.5-flash",
        messages: [
          { role: "system", content: systemPrompt },
          { role: "user", content: userPrompt },
        ],
        response_format: { type: "json_object" },
      }),
    });

    if (!res.ok) {
      if (res.status === 429) return { slugs: [], reason: "Demasiadas consultas. Intenta en un momento." };
      if (res.status === 402) return { slugs: [], reason: "Servicio de IA no disponible." };
      return { slugs: [], reason: "No pudimos consultar a la IA." };
    }

    const data = (await res.json()) as {
      choices?: { message?: { content?: string } }[];
    };
    const content = data.choices?.[0]?.message?.content ?? "{}";
    const parsed = JSON.parse(content) as { slugs?: unknown; reason?: unknown };
    const slugs = Array.isArray(parsed.slugs)
      ? (parsed.slugs as unknown[])
          .map((s) => String(s))
          .filter((s) => ALL_SLUGS.includes(s))
      : [];
    const reason = typeof parsed.reason === "string" ? parsed.reason : "";
    return { slugs, reason };
  } catch {
    return { slugs: [], reason: "No pudimos consultar a la IA." };
  }
}

const LANG_LABEL: Record<string, string> = {
  es: "en español",
  en: "in English",
  fr: "en français",
  pt: "em português",
  it: "in italiano",
};

export const matchDestinationsByText = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) =>
    z
      .object({
        query: z.string().min(1).max(500),
        lang: z.enum(["es", "en", "fr", "pt", "it", "de"]).default("es"),
      })
      .parse(input),
  )
  .handler(async ({ data }) => {
    const ip = getClientIp(getRequest());
    const rl = rateLimit(`explora-match:${ip}`, { windowMs: 60_000, max: 20 });
    if (!rl.ok) {
      return { slugs: [], reason: "Demasiadas consultas. Intenta en un momento." };
    }
    const langLabel = LANG_LABEL[data.lang] ?? LANG_LABEL.es;
    const system = `Eres un curador de viajes de "Mostrando México". Conoces estos destinos:

${buildDestinationContext()}

Tarea: Dado el texto del usuario, elige hasta 6 slugs de la lista que mejor coincidan, ordenados de mejor a peor. Solo slugs exactos de la lista. Devuelve JSON estricto:
{"slugs": ["slug1","slug2"], "reason": "una frase breve ${langLabel} explicando por qué"}

Si la consulta no es de viajes, devuelve {"slugs": [], "reason": "..."}.`;
    return await callAi(system, data.query);
  });


const QuestionnaireSchema = z.object({
  vibe: z
    .array(
      z.enum([
        "playa",
        "cultura",
        "naturaleza",
        "aventura",
        "islas",
        "arqueologia",
        "gastronomia",
        "cenotes",
        "cultura-maya",
        "vida-nocturna",
        "familiar",
        "romance",
      ] as const),
    )
    .default([]),
  pace: z.enum(["tranquilo", "balanceado", "intenso"]).default("balanceado"),
  company: z.enum(["solo", "pareja", "familia", "amigos"]).default("pareja"),
  extra: z.string().max(300).optional(),
  lang: z.enum(["es", "en", "fr", "pt", "it", "de"]).default("es"),
});

export const recommendDestinations = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) => QuestionnaireSchema.parse(input))
  .handler(async ({ data }) => {
    const ip = getClientIp(getRequest());
    const rl = rateLimit(`explora-recommend:${ip}`, { windowMs: 60_000, max: 20 });
    if (!rl.ok) {
      return { slugs: [], reason: "Demasiadas consultas. Intenta en un momento." };
    }
    const langLabel = LANG_LABEL[data.lang] ?? LANG_LABEL.es;

    const summary = [
      `Categorías deseadas: ${data.vibe.join(", ") || "ninguna en particular"}`,
      `Ritmo: ${data.pace}`,
      `Con quién viaja: ${data.company}`,
      data.extra ? `Notas: ${data.extra}` : null,
    ]
      .filter(Boolean)
      .join("\n");

    const system = `Eres un curador de viajes de "Mostrando México". Conoces estos destinos:

${buildDestinationContext()}

Categorías válidas: ${EXPLORA_CATEGORIES.join(", ")}.

Tarea: A partir del perfil del usuario, elige hasta 6 slugs de la lista, ordenados de mejor a peor. Devuelve JSON estricto:
{"slugs": ["slug1","slug2"], "reason": "una frase breve ${langLabel} explicando la lógica"}`;

    return await callAi(system, summary);
  });

/** Local fallback: rule-based ranking from selected category filters. */
export function localRankByCategories(categories: ExploraCategory[]): string[] {
  if (categories.length === 0) return [];
  return [...EXPLORA_DESTINATIONS]
    .map((d) => ({
      slug: d.slug,
      score: d.categories.filter((c) => categories.includes(c)).length,
    }))
    .filter((d) => d.score > 0)
    .sort((a, b) => b.score - a.score)
    .map((d) => d.slug);
}
